import Link from 'next/link';

interface DiveCentresPreviewProps {
  className?: string;
}

const regions = [
  {
    name: 'Larnaca',
    slug: '/dive-sites/larnaca',
    highlight: 'Zenobia wreck — world-class wreck diving',
    centres: '6+ dive centres',
    icon: (
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" className="w-5 h-5" aria-hidden="true">
        <path d="M12 22s-8-4.5-8-11.8A8 8 0 0 1 12 2a8 8 0 0 1 8 8.2c0 7.3-8 11.8-8 11.8z"/>
        <circle cx="12" cy="10" r="3"/>
      </svg>
    ),
  },
  {
    name: 'Limassol',
    slug: '/dive-sites/limassol',
    highlight: 'Jubilee Shoals, reefs & wall dives',
    centres: '5+ dive centres',
    icon: (
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" className="w-5 h-5" aria-hidden="true">
        <path d="M12 22s-8-4.5-8-11.8A8 8 0 0 1 12 2a8 8 0 0 1 8 8.2c0 7.3-8 11.8-8 11.8z"/>
        <circle cx="12" cy="10" r="3"/>
      </svg>
    ),
  },
  {
    name: 'Paphos',
    slug: '/dive-sites/paphos',
    highlight: 'Caves, reefs & shore diving',
    centres: '8+ dive centres',
    icon: (
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" className="w-5 h-5" aria-hidden="true">
        <path d="M12 22s-8-4.5-8-11.8A8 8 0 0 1 12 2a8 8 0 0 1 8 8.2c0 7.3-8 11.8-8 11.8z"/>
        <circle cx="12" cy="10" r="3"/>
      </svg>
    ),
  },
  {
    name: 'Ayia Napa & Protaras',
    slug: '/dive-sites/ayia-napa-protaras',
    highlight: 'Sea caves, MUSAN & clear water',
    centres: '6+ dive centres',
    icon: (
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" className="w-5 h-5" aria-hidden="true">
        <path d="M12 22s-8-4.5-8-11.8A8 8 0 0 1 12 2a8 8 0 0 1 8 8.2c0 7.3-8 11.8-8 11.8z"/>
        <circle cx="12" cy="10" r="3"/>
      </svg>
    ),
  },
  {
    name: 'Akamas & Latchi',
    slug: '/dive-sites/akamas-latchi',
    highlight: 'Remote reefs, turtles & pristine water',
    centres: '3+ dive centres',
    icon: (
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" className="w-5 h-5" aria-hidden="true">
        <path d="M12 22s-8-4.5-8-11.8A8 8 0 0 1 12 2a8 8 0 0 1 8 8.2c0 7.3-8 11.8-8 11.8z"/>
        <circle cx="12" cy="10" r="3"/>
      </svg>
    ),
  },
];

const DiveCentresPreview = ({ className = '' }: DiveCentresPreviewProps) => {
  return (
    <section
      className={`py-12 sm:py-14 md:py-16 ${className}`}
      style={{
        background: '#0A1520',
        borderTop: '1px solid rgba(255,255,255,0.06)',
      }}
    >
      <div className="container mx-auto px-4 sm:px-6">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4 mb-8 sm:mb-10">
          <div>
            <p
              className="text-xs font-semibold uppercase tracking-widest mb-2"
              style={{ color: '#FF8A3D' }}
            >
              Find a Dive Centre
            </p>
            <h2
              className="text-2xl sm:text-3xl md:text-4xl font-headline font-bold"
              style={{ color: '#EAF4F8' }}
            >
              Dive Centres Across Cyprus
            </h2>
            <p
              className="text-sm sm:text-base mt-2 max-w-xl"
              style={{ color: '#C8DDE6' }}
            >
              Browse certified dive operators by region — with practical information to help you choose where to dive.
            </p>
          </div>
          <Link
            href="/dive-centres-directory"
            className="inline-flex items-center gap-2 text-sm font-medium whitespace-nowrap transition-opacity hover:opacity-80 self-start sm:self-auto"
            style={{ color: '#6FD3E0' }}
          >
            Full directory
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="w-4 h-4" aria-hidden="true">
              <path d="M5 12h14M12 5l7 7-7 7"/>
            </svg>
          </Link>
        </div>

        {/* Region grid */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {regions.map((region) => (
            <Link
              key={region.name}
              href={region.slug}
              className="group rounded-xl p-5 flex items-start gap-4 transition-all duration-200"
              style={{
                background: '#0F2234',
                border: '1px solid rgba(255,255,255,0.06)',
              }}
              onMouseEnter={(e) => {
                const el = e.currentTarget as HTMLElement;
                el.style.border = '1px solid rgba(111,211,224,0.25)';
                el.style.background = '#112638';
              }}
              onMouseLeave={(e) => {
                const el = e.currentTarget as HTMLElement;
                el.style.border = '1px solid rgba(255,255,255,0.06)';
                el.style.background = '#0F2234';
              }}
            >
              {/* Icon */}
              <div
                className="w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0"
                style={{
                  background: 'rgba(111,211,224,0.10)',
                  color: '#6FD3E0',
                  border: '1px solid rgba(111,211,224,0.20)',
                }}
              >
                {region.icon}
              </div>

              {/* Text */}
              <div className="min-w-0">
                <div className="flex items-center gap-2 mb-1">
                  <h3
                    className="font-headline font-semibold text-base group-hover:text-[#6FD3E0] transition-colors"
                    style={{ color: '#EAF4F8' }}
                  >
                    {region.name}
                  </h3>
                </div>
                <p className="text-xs mb-2" style={{ color: '#C8DDE6' }}>
                  {region.highlight}
                </p>
                <span
                  className="text-xs font-medium"
                  style={{ color: '#6B8FA0' }}
                >
                  {region.centres}
                </span>
              </div>

              {/* Arrow */}
              <svg
                viewBox="0 0 24 24"
                fill="none"
                stroke="#6FD3E0"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
                className="w-4 h-4 flex-shrink-0 ml-auto opacity-0 group-hover:opacity-100 transition-opacity"
                aria-hidden="true"
              >
                <path d="M5 12h14M12 5l7 7-7 7"/>
              </svg>
            </Link>
          ))}

          {/* Full directory CTA card */}
          <Link
            href="/dive-centres-directory"
            className="group rounded-xl p-5 flex items-center justify-center gap-3 transition-all duration-200"
            style={{
              background: 'rgba(111,211,224,0.05)',
              border: '1px dashed rgba(111,211,224,0.25)',
            }}
            onMouseEnter={(e) => {
              const el = e.currentTarget as HTMLElement;
              el.style.background = 'rgba(111,211,224,0.10)';
              el.style.border = '1px dashed rgba(111,211,224,0.45)';
            }}
            onMouseLeave={(e) => {
              const el = e.currentTarget as HTMLElement;
              el.style.background = 'rgba(111,211,224,0.05)';
              el.style.border = '1px dashed rgba(111,211,224,0.25)';
            }}
          >
            <svg
              viewBox="0 0 24 24"
              fill="none"
              stroke="#6FD3E0"
              strokeWidth="1.8"
              strokeLinecap="round"
              strokeLinejoin="round"
              className="w-5 h-5"
              aria-hidden="true"
            >
              <rect x="3" y="3" width="7" height="7"/><rect x="14" y="3" width="7" height="7"/><rect x="14" y="14" width="7" height="7"/><rect x="3" y="14" width="7" height="7"/>
            </svg>
            <span className="font-medium text-sm" style={{ color: '#6FD3E0' }}>
              Browse full directory
            </span>
          </Link>
        </div>
      </div>
    </section>
  );
};

export default DiveCentresPreview;
